import { sendGTMEvent } from "shared/services/GTM";
import { GTMEventParams } from "shared/services/GTM/types";
import { getPlatformType } from "shared/utils/platform";

export const AUTH_RETURN_URL_KEY = "voopter.auth.returnUrl";

export const AUTH_SERVER_URL =
  window.location.hostname === "localhost"
    ? "http://localhost:5174/"
    : "https://autenticacao.voopter.com.br/";
const getUserApiUrl = () => {
  const hostname = window.location.hostname.toLowerCase();

  if (hostname.includes("staging")) {
    return "https://user-api-staging.voopter.com.br";
  }

  if (hostname === "localhost") {
    return (
      process.env.REACT_APP_USER_API_URL ??
      "https://user-api-staging.voopter.com.br"
    );
  }

  return (
    process.env.REACT_APP_USER_API_URL ?? "https://user-api.voopter.com.br"
  );
};

const USER_API_URL = getUserApiUrl();
export const USER_API_AUTHORIZATION =
  process.env.REACT_APP_USER_API_AUTHORIZATION ??
  "6%F77R@Pr@298c*&Fh9iy$9$$DZToP4@";

export type AuthUser = {
  id: string;
  email: string;
  name: string | null;
  whatsappNumber?: string | null;
  [key: string]: unknown;
};

type UserProfileResponse = {
  profileData: AuthUser;
};

export type NotificationPreferences = {
  isEmailPromotionalNotifications?: boolean | number | string;
  isEmailAlertNotifications?: boolean | number;
  isEmailFindingsNotifications?: boolean | number;
  isEmailPartnerEmailsNotifications?: boolean | number;
  isWhatsAppPromotionalNotifications?: boolean | number | string;
  isWhatsAppAlertNotifications?: boolean | number;
  isWhatsAppFindingsNotifications?: boolean | number;
  isWhatsAppPartnerEmailsNotifications?: boolean | number;
};

type UserPreferencesResponse = {
  userPreferencesData: NotificationPreferences;
};

type SessionErrorCode =
  | "SESSION_REQUIRED"
  | "SESSION_INVALID"
  | "SESSION_USER_NOT_FOUND";

const SESSION_ERROR_CODES = new Set<SessionErrorCode>([
  "SESSION_REQUIRED",
  "SESSION_INVALID",
  "SESSION_USER_NOT_FOUND",
]);

let sessionExpirationEventSent = false;

const isSessionErrorCode = (code?: string): code is SessionErrorCode =>
  Boolean(code && SESSION_ERROR_CODES.has(code as SessionErrorCode));

const PROTECTED_AUTH_PATHS = new Set([
  "/auth/callback",
  "/dados-pessoais",
  "/preferencias-notificacao",
  "/privacidade",
]);

const authenticatedRequest = async (path: string, init?: RequestInit) => {
  const response = await fetch(`${USER_API_URL}${path}`, {
    ...init,
    credentials: "include",
    headers: {
      Accept: "application/json",
      Authorization: USER_API_AUTHORIZATION,
      ...init?.headers,
    },
  });

  if (response.status === 401) {
    const data = (await response
      .clone()
      .json()
      .catch(() => null)) as {
      code?: string;
    } | null;
    const currentPath = window.location.pathname;

    if (
      isSessionErrorCode(data?.code) &&
      PROTECTED_AUTH_PATHS.has(currentPath)
    ) {
      if (!sessionExpirationEventSent) {
        sessionExpirationEventSent = true;
        sendGTMEvent("authSessionExpired", {
          platform: getPlatformType(),
          errorCode: data.code,
          pagePath: currentPath,
        });
      }
      window.location.replace("/");
    }
  }

  return response;
};

type StartLoginOptions = {
  source: GTMEventParams["authLoginStart"]["source"];
  // Protected pages redirect to the login on their own. Replacing their
  // history entry keeps the back button from landing on them again, which
  // would start a new login right away.
  replaceCurrentPage?: boolean;
};

// One authLoginStart per login attempt: repeated calls on the same page
// (double clicks, effects running twice) count once. A page restored from
// the back-forward cache starts a new attempt.
let loginStartTracked = false;

window.addEventListener("pageshow", (event) => {
  if (event.persisted) loginStartTracked = false;
});

export function startLogin({
  source,
  replaceCurrentPage = false,
}: StartLoginOptions) {
  if (!loginStartTracked) {
    loginStartTracked = true;
    sendGTMEvent("authLoginStart", {
      platform: getPlatformType(),
      source,
      pagePath: window.location.pathname,
    });
  }

  const currentUrl = `${window.location.pathname}${window.location.search}${window.location.hash}`;
  sessionStorage.setItem(AUTH_RETURN_URL_KEY, currentUrl);

  const callbackUrl = `${window.location.origin}/auth/callback`;
  const loginUrl = new URL(AUTH_SERVER_URL);
  loginUrl.searchParams.set("clientId", window.location.hostname);
  loginUrl.searchParams.set("callback", callbackUrl);

  if (replaceCurrentPage) {
    window.location.replace(loginUrl.toString());
  } else {
    window.location.assign(loginUrl.toString());
  }
}

export function consumeReturnUrl() {
  const returnUrl = sessionStorage.getItem(AUTH_RETURN_URL_KEY);
  sessionStorage.removeItem(AUTH_RETURN_URL_KEY);

  if (!returnUrl) return "/";

  const resolvedUrl = new URL(returnUrl, window.location.origin);
  if (resolvedUrl.origin !== window.location.origin) return "/";

  return `${resolvedUrl.pathname}${resolvedUrl.search}${resolvedUrl.hash}`;
}

export async function getSessionUser(): Promise<AuthUser | null> {
  const response = await authenticatedRequest("/user-profile");

  if (response.status === 401) return null;

  if (!response.ok) {
    throw new Error("Não foi possível consultar a sessão autenticada.");
  }

  const data = (await response.json()) as UserProfileResponse;
  return data.profileData;
}

export async function logout() {
  const response = await authenticatedRequest("/logout", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
  });

  if (!response.ok) {
    throw new Error("Não foi possível sair. Tente novamente.");
  }
}

async function assertSuccessfulResponse(response: Response) {
  if (response.ok) return;

  const data = (await response.json().catch(() => null)) as {
    message?: string;
  } | null;
  throw new Error(data?.message || "Não foi possível salvar as alterações.");
}

export async function updateUserProfile(data: {
  name: string;
  whatsappNumber: string;
}) {
  const response = await authenticatedRequest("/update-user-profile", {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });

  await assertSuccessfulResponse(response);
}

export async function getNotificationPreferences() {
  const response = await authenticatedRequest("/user-preferences");

  if (response.status === 404) {
    return {
      isEmailPromotionalNotifications: false,
      isWhatsAppPromotionalNotifications: false,
    } satisfies NotificationPreferences;
  }

  await assertSuccessfulResponse(response);

  const data = (await response.json()) as UserPreferencesResponse;
  return data.userPreferencesData;
}

export async function updateNotificationPreferences(
  preferences: NotificationPreferences,
) {
  const response = await authenticatedRequest("/update-user-preferences", {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(preferences),
  });

  await assertSuccessfulResponse(response);
}

export async function requestAccountDeletion() {
  const response = await authenticatedRequest("/request-delete-account", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
  });

  await assertSuccessfulResponse(response);
}

export async function validateAccountDeletion(deletionToken: string) {
  const response = await authenticatedRequest(
    `/delete-account/${encodeURIComponent(deletionToken)}`,
    { method: "DELETE" },
  );

  await assertSuccessfulResponse(response);
}
